import { z } from "zod";
import { ArtistIntakeSchema, ConsentAcceptanceSchema, DarkRoomReviewSchema } from "./schemas";
import { AssessmentEnvelopeSchema } from "./assessment";
import { receiptResourceCheck } from "./receiptSchema";
import { LocalRunRecordSchema, WorkspaceContextSchema } from "./localRun";
import { ProjectActionLedgerSchema, PROJECT_ACTION_MAX_BYTES } from "./projectActions";
import { ReleaseDraftHistorySchema } from "./releaseDraftHistory";
import { ProjectHistorySchema, PROJECT_HISTORY_MAX_BYTES } from "./projectHistory";

export const LOCAL_STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const LOCAL_STATE_MODE_KEY = "shadow-storage-mode";
export type LocalStorageMode = "memory" | "browser";
export interface StoragePort { readonly length: number; key(index: number): string | null; getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
const score = z.number().finite().min(0).max(100);
const texts = z.array(z.string().max(4096)).max(64);
const draft = ArtistIntakeSchema.extend({
  artistName: z.string().max(300), email: z.string().max(320), genre: z.string().max(100).optional(),
  links: z.array(z.string().max(2048)).max(20), goals: z.array(z.string().max(300)).max(20), songText: z.string().max(12000).optional(),
  previousRelease: z.object({ title: z.string().max(300), releasedAt: z.string().max(100), platform: z.string().max(100) }).optional()
}).strict();
const historyEntry = z.object({ ts: z.number().finite().min(0), artistName: z.string().max(300), readinessScore: score, overallScore: score,
  personaScores: z.record(z.string().max(100), score).optional(), openGates: texts.optional(), assessment: AssessmentEnvelopeSchema.optional(),
  runId: z.string().min(1).max(200).optional(), artistRecordId: z.string().min(1).max(200).optional(), projectId: z.string().min(1).max(200).optional()
}).strict();
const jsonSchemas: Record<string, z.ZodType> = {
  "shadow-intake": draft,
  "shadow-consent": ConsentAcceptanceSchema.extend({ version: z.literal("shadow-consent-v1") }).strict(),
  "shadow-review": DarkRoomReviewSchema.refine(r => Object.values(r.compositeScores).every(s => s === undefined || (Number.isFinite(s) && s >= 0 && s <= 100)) && r.assessment?.mode !== "human_review", "invalid score or unsupported producer"),
  "shadow-workspace-context": WorkspaceContextSchema,
  "shadow-assessment-run": LocalRunRecordSchema,
  "shadow-project-actions": ProjectActionLedgerSchema,
  "shadow-release-drafts": ReleaseDraftHistorySchema,
  "shadow-project-history": ProjectHistorySchema,
  "shadow-action-status": z.record(z.string().max(4096), z.enum(["open", "planned", "done"])),
  "shadow-action-streak": z.object({ lastDate: z.string().max(10), streak: z.number().int().min(0).max(100000) }).strict(),
  "shadow-signal-history": z.array(historyEntry).max(5),
  "shadow-last-review-snapshot": z.object({ intake: draft, openGates: texts, readinessScore: score }).strict()
};
const preferences: Record<string, readonly string[] | RegExp> = {
  "shadow-theme": ["dark", "light", "eclipse"],
  "shadow-journey-step": ["landing", "intake", "consent", "dark_room", "signal", "release_room", "package"],
  "shadow-signal-tab": ["report", "actions", "capsule", "history"], "shadow-action-sort": ["urgency", "easiest"],
  "shadow-dark-room-runs": /^\d{1,6}$/, "shadow-onboarding-complete": ["1"], "signalGuideShown": ["1"]
};
export const ARTIST_LOCAL_JSON_KEYS = Object.freeze(Object.keys(jsonSchemas));
const envelope = z.object({ schemaVersion: z.literal(1), savedAt: z.number().finite().min(0), expiresAt: z.number().finite().min(0), value: z.unknown() }).strict();
export type LocalStateStatus = { mode: LocalStorageMode; state: "session_only" | "saved" | "save_failed" | "recovery_required"; issues: string[]; legacy: boolean };
export type RecordStorageState = "missing" | "session_only" | "saved" | "save_failed" | "recovery_required";

/** Browser records are untrusted continuity data. They never establish identity or server deletion. */
export class BrowserState {
  private memory = new Map<string, string>();
  private issues = new Set<string>();
  private legacy = false;
  private failed = false;
  private failedKeys = new Set<string>();
  private mode: LocalStorageMode = "memory";
  private listeners = new Set<() => void>();
  private ignoreDiskPreferences = false;
  private revision = 0;
  constructor(private local?: StoragePort, private session?: StoragePort, private now = () => Date.now(), private unavailableStores: string[] = []) {
    try {
      const storedMode = local?.getItem(LOCAL_STATE_MODE_KEY);
      if (storedMode !== undefined && storedMode !== null) {
        const parsed = z.object({ schemaVersion: z.literal(1), mode: z.enum(["memory", "browser"]) }).strict().safeParse(JSON.parse(storedMode));
        if (parsed.success) this.mode = parsed.data.mode;
        else this.issues.add("unsupported_storage_mode");
      } else if (ARTIST_LOCAL_JSON_KEYS.some(k => local?.getItem(k) != null)) this.mode = "browser";
      if (this.mode === "browser") for (const key of ARTIST_LOCAL_JSON_KEYS) {
        const raw = local?.getItem(key);
        if (raw !== undefined && raw !== null) {
          const parsed = parseArtifact(key, raw, this.now());
          if (parsed.ok) { this.memory.set(key, JSON.stringify(parsed.value)); this.legacy ||= parsed.legacy; }
          else this.issues.add(`${key}: ${parsed.reason}`);
        }
      }
    } catch { this.failed = true; this.mode = "memory"; }
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  snapshot = () => JSON.stringify(this.status());
  status(): LocalStateStatus {
    return { mode: this.mode, state: this.issues.size ? "recovery_required" : this.failed || this.failedKeys.size ? "save_failed" : this.mode === "browser" ? "saved" : "session_only", issues: [...this.issues, ...this.failedKeys].sort(), legacy: this.legacy };
  }
  recordState(key: string): RecordStorageState {
    if (this.issues.size) return "recovery_required";
    if (this.failedKeys.has(key)) return "save_failed";
    const current = this.memory.get(key);
    if (!current) return "missing";
    if (this.mode === "memory") return "session_only";
    try {
      const raw = this.local?.getItem(key);
      if (!raw) return "save_failed";
      const parsed = parseArtifact(key, raw, this.now());
      if (!parsed.ok) return parsed.reason === "expired" ? "recovery_required" : "save_failed";
      return JSON.stringify(parsed.value) === current ? "saved" : "save_failed";
    } catch { return "save_failed"; }
  }
  recordSnapshot(key: string): string { return JSON.stringify({ revision: this.revision, state: this.recordState(key) }); }
  private emit() { this.revision++; for (const listener of this.listeners) listener(); }
  readJson<T>(key: string, fallback: T): T {
    if (this.issues.size) return fallback;
    const raw = this.memory.get(key);
    if (!raw) return fallback;
    const parsed = parseArtifact(key, raw, this.now());
    return parsed.ok ? parsed.value as T : fallback;
  }
  writeJson(key: string, value: unknown): boolean {
    if (this.issues.size) return false; // Never overwrite quarantined recovery evidence with initial empty React state.
    let raw: string;
    try { raw = JSON.stringify(value); } catch { this.failedKeys.add(key); this.emit(); return false; }
    const parsed = parseArtifact(key, raw, this.now());
    if (!parsed.ok) { this.failedKeys.add(key); this.emit(); return false; }
    this.memory.set(key, JSON.stringify(parsed.value));
    if (this.mode === "memory") this.failedKeys.delete(key);
    const ok = this.mode === "memory" || this.persist(key, parsed.value);
    this.emit(); return ok;
  }
  readPreference(key: string): string | null {
    if (this.issues.size) return null;
    try {
      const value = this.memory.get(key) ?? (this.ignoreDiskPreferences ? null : this.local?.getItem(key)) ?? null;
      if (key === "shadow-theme" && value === "void") return "eclipse";
      return validPreference(key, value) ? value : null;
    } catch { this.failed = true; return null; }
  }
  writePreference(key: string, value: string): boolean {
    if (this.issues.size || !validPreference(key, value)) return false;
    this.memory.set(key, value);
    if (this.mode === "browser") try { if (!this.local) throw new Error(); this.local.setItem(key, value); this.failedKeys.delete(key); } catch { this.failedKeys.add(key); this.emit(); return false; }
    return true;
  }
  setMode(mode: LocalStorageMode): boolean {
    if (this.issues.size) return false;
    this.mode = mode; this.failed = false;
    try {
      if (!this.local) throw new Error();
      if (mode === "memory") for (const key of ARTIST_LOCAL_JSON_KEYS) this.local.removeItem(key);
      this.local.setItem(LOCAL_STATE_MODE_KEY, JSON.stringify({ schemaVersion: 1, mode }));
      if (mode === "browser") for (const [key, raw] of this.memory) {
        if (jsonSchemas[key]) { if (!this.persist(key, JSON.parse(raw))) throw new Error(); }
        else if (validPreference(key, raw)) this.local.setItem(key, raw);
      }
      if (mode === "browser") this.legacy = false;
    } catch { this.failed = true; }
    this.emit(); return !this.failed && this.failedKeys.size === 0;
  }
  private persist(key: string, value: unknown): boolean {
    try {
      if (!this.local) throw new Error();
      const savedAt = this.now();
      this.local.setItem(key, JSON.stringify({ schemaVersion: 1, savedAt, expiresAt: savedAt + LOCAL_STATE_TTL_MS, value }));
      this.failedKeys.delete(key);
      return true;
    } catch { this.failedKeys.add(key); return false; }
  }
  /** Includes only known artist records/preferences; authentication credentials are never exported. */
  recoveryCopy(): string {
    const records: Record<string, string> = {};
    for (const key of [...ARTIST_LOCAL_JSON_KEYS, ...Object.keys(preferences)]) {
      let raw: string | null | undefined;
      try { raw = this.issues.size ? this.local?.getItem(key) : this.memory.get(key) ?? this.local?.getItem(key); }
      catch { throw new Error("Browser storage cannot be read. Download the current Signal receipt if available."); }
      if (raw !== undefined && raw !== null) records[key] = raw;
    }
    const data = JSON.stringify({ format: "shadow-local-recovery-v1", exportedAt: new Date(this.now()).toISOString(), trust: "Untrusted recovery backup; not an authenticated receipt or automatic restore.", records }, null, 2);
    if (new TextEncoder().encode(data).byteLength > 4 * 1024 * 1024) throw new Error("Recovery data exceeds the safe download limit. Preserve it using browser site-data tools before erasing.");
    return data;
  }
  /** Clears this origin's SHADOW namespace in both stores; it does not touch unrelated apps or remote records. */
  erase(): { ok: boolean; remainingKeys: string[] } {
    this.ignoreDiskPreferences = true;
    this.memory.clear(); this.mode = "memory"; this.issues.clear(); this.legacy = false; this.failed = false; this.failedKeys.clear();
    const remainingKeys: string[] = [...this.unavailableStores];
    for (const storage of [this.local, this.session]) {
      if (!storage) continue;
      try {
        const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i)).filter((k): k is string => !!k && ownsKey(k));
        for (const key of keys) try { storage.removeItem(key); } catch { remainingKeys.push(key); }
        for (const key of keys) if (storage.getItem(key) !== null && !remainingKeys.includes(key)) remainingKeys.push(key);
      } catch { remainingKeys.push("unreadable_browser_storage"); }
    }
    this.failed = remainingKeys.length > 0; this.emit();
    return { ok: !this.failed, remainingKeys };
  }
}
export function parseArtifact(key: string, raw: string, now: number): { ok: true; value: unknown; legacy: boolean } | { ok: false; reason: string } {
  if (!jsonSchemas[key]) return { ok: false, reason: "unsupported_key" };
  try {
    const maxBytes = key === "shadow-project-history" ? PROJECT_HISTORY_MAX_BYTES + 512 : key === "shadow-project-actions" ? PROJECT_ACTION_MAX_BYTES + 512 : 128 * 1024;
    if (new TextEncoder().encode(raw).byteLength > maxBytes) return { ok: false, reason: "oversized" };
    let value: unknown = JSON.parse(raw);
    // Actions have their own strictly bounded, nonrecursive schema and complete-ledger byte cap.
    // Receipt array limits would otherwise reject valid multi-run action history.
    if (key !== "shadow-project-actions" && key !== "shadow-project-history" && receiptResourceCheck(value, { maxStringCharacters: 12000, maxArrayItems: key === "shadow-assessment-run" ? 150 : 64 })) return { ok: false, reason: "invalid_resource_shape" };
    let legacy = true;
    if (value && typeof value === "object" && !Array.isArray(value) && "schemaVersion" in value) {
      const parsed = envelope.safeParse(value);
      if (!parsed.success || parsed.data.expiresAt <= parsed.data.savedAt || parsed.data.expiresAt - parsed.data.savedAt > LOCAL_STATE_TTL_MS || parsed.data.savedAt > now + 60000) return { ok: false, reason: "unsupported_envelope" };
      if (parsed.data.expiresAt <= now) return { ok: false, reason: "expired" };
      value = parsed.data.value; legacy = false;
    }
    const parsed = jsonSchemas[key].safeParse(value);
    return parsed.success ? { ok: true, value: parsed.data, legacy } : { ok: false, reason: "invalid_record" };
  } catch { return { ok: false, reason: "invalid_json" }; }
}
function validPreference(key: string, value: string | null): value is string {
  const rule = preferences[key]; return value !== null && !!rule && (rule instanceof RegExp ? rule.test(value) : rule.includes(value));
}
function ownsKey(key: string) { return key.startsWith("shadow-") || key === "signalGuideShown"; }
